/**
 * Integration GraphQL Mutations
 *
 * 7 mutations for external system connectivity:
 * - createIntegration
 * - updateHealthStatus
 * - updateCircuitBreakerState
 * - testIntegrationConnection
 * - resetCircuitBreaker
 * - rotateCredentials
 * - disableIntegration
 *
 * @see graphql_gateway/src/integration_api/integration_graphql.rs - Backend implementation
 */

import { gql } from '@apollo/client';
import {
  INTEGRATION_FIELDS,
  HEALTH_CHECK_RESULT_FIELDS,
  CONNECTION_TEST_RESULT_FIELDS,
} from './fragments';

/** Create a new integration with backend Snowflake ID */
export const CREATE_INTEGRATION = gql`
  ${INTEGRATION_FIELDS}
  mutation CreateIntegration($input: CreateIntegrationInput!) {
    createIntegration(input: $input) {
      ...IntegrationFields
    }
  }
`;

/** Update integration health status (called by health check service) */
export const UPDATE_HEALTH_STATUS = gql`
  ${HEALTH_CHECK_RESULT_FIELDS}
  mutation UpdateHealthStatus($input: UpdateHealthStatusInput!) {
    updateHealthStatus(input: $input) {
      ...HealthCheckResultFields
    }
  }
`;

/** Update circuit breaker state */
export const UPDATE_CIRCUIT_BREAKER_STATE = gql`
  ${INTEGRATION_FIELDS}
  mutation UpdateCircuitBreakerState($input: UpdateCircuitBreakerInput!) {
    updateCircuitBreakerState(input: $input) {
      ...IntegrationFields
    }
  }
`;

/** Test integration connection (validates connectivity without affecting health metrics) */
export const TEST_INTEGRATION_CONNECTION = gql`
  ${CONNECTION_TEST_RESULT_FIELDS}
  mutation TestIntegrationConnection($integrationId: String!) {
    testIntegrationConnection(integrationId: $integrationId) {
      ...ConnectionTestResultFields
    }
  }
`;

/** Manually trigger circuit breaker reset (for incident response) */
export const RESET_CIRCUIT_BREAKER = gql`
  ${INTEGRATION_FIELDS}
  mutation ResetCircuitBreaker($integrationId: String!) {
    resetCircuitBreaker(integrationId: $integrationId) {
      ...IntegrationFields
    }
  }
`;

/** Rotate integration credentials (triggers credential refresh) */
export const ROTATE_CREDENTIALS = gql`
  ${INTEGRATION_FIELDS}
  mutation RotateCredentials($integrationId: String!) {
    rotateCredentials(integrationId: $integrationId) {
      ...IntegrationFields
    }
  }
`;

/** Disable integration (emergency kill switch) */
export const DISABLE_INTEGRATION = gql`
  ${INTEGRATION_FIELDS}
  mutation DisableIntegration($integrationId: String!, $reason: String!) {
    disableIntegration(integrationId: $integrationId, reason: $reason) {
      ...IntegrationFields
    }
  }
`;
